import React from 'react';
import {
  AssessmentTestResult,
  AssessmentTestStep,
  AssessmentTestStepAnswer,
} from '../../../../api/admin';
import Table from '../../../Table/CompactTable';
import { createColumnHelper } from '@tanstack/react-table';
import dayjs from 'dayjs';
import {
  CollapsibleSectionFooter,
  CollapsibleSectionFooterButton,
  CollapsibleSectionLabel,
} from '../../CollapsibleSection';
import styled from 'styled-components';
import QuestionStatusCell from './QuestionStatusCell';
import QuestionTypeCell from './QuestionTypeCell';
import QuestionTextCell from './QuestionTextCell';
import QuestionAnswerCell from './QuestionAnswerCell';
import QuestionSkillsReviewCell from './QuestionSkillsReviewCell';
import ScenarioStepModal from './ScenarioStepModal';
import QuestionAnalysisModal from './QuestionAnalysisModal';
import { formatDuration } from '../common';

const TableWrapper = styled.div`
  .step-questions-table {
    th,
    td {
      &:last-child {
        border-right: none !important;
      }
      &:first-child {
        border-left: none !important;
      }
    }

    tbody > tr:last-child > td {
      border-bottom: none;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }

    tbody > tr {
      transition: background-color 0.2s ease;

      &:hover {
        background-color: ${({ theme }) => theme.colors.bgDefaultSecondary};
      }
    }

    th {
      border-radius: 0 !important;
    }
  }
`;

export type TestQuestionRow = {
  id: string;
  didStartTest: boolean;
  stepId: string;
  stepReview: AssessmentTestResult['scenarioEvaluations'][number] | null;
  stepObjectives: AssessmentTestStep['objectives'];
  secondsSpent: number | null;
  questionNumber: number;
  section: AssessmentTestStep['sections'][number];
  answer: AssessmentTestStepAnswer['sections'][number] | null;
  dimensions: Array<{ id: string; name: string; label: string }>;
};

const computeTimeFromIntervals = (
  intervals: Array<{ startedAt: Date; endedAt: Date | null }> | undefined
): number => {
  if (!intervals || intervals.length === 0) {
    return 0;
  }
  return intervals.reduce((acc, interval) => {
    return (
      acc +
      dayjs(interval.endedAt ?? new Date()).diff(
        dayjs(interval.startedAt),
        'seconds'
      )
    );
  }, 0);
};

const columnHelper = createColumnHelper<TestQuestionRow>();

const createColumns = () => [
  columnHelper.display({
    id: 'questionNumber',
    header: 'Title',
    enableSorting: false,
    cell: (props) => `Question ${props.row.original.questionNumber}`,
    size: 130,
    meta: {
      styles: {
        minWidth: '130px',
        maxWidth: '130px',
        width: '130px',
      },
    },
  }),

  columnHelper.display({
    id: 'status',
    header: 'Status',
    cell: QuestionStatusCell,
    size: 180,
    meta: {
      styles: {
        minWidth: '180px',
        maxWidth: '180px',
        width: '180px',
      },
    },
  }),
  columnHelper.display({
    id: 'questionText',
    header: 'Question',
    cell: QuestionTextCell,
    meta: {
      styles: {
        minWidth: '300px',
        maxWidth: '300px',
        width: '300px',
      },
    },
  }),
  columnHelper.display({
    id: 'questionAnswer',
    header: 'Answer',
    enableSorting: false,
    cell: QuestionAnswerCell,
    meta: {
      styles: {
        minWidth: '300px',
        maxWidth: '300px',
        width: '300px',
      },
    },
  }),
  columnHelper.display({
    id: 'type',
    header: 'Type',
    cell: QuestionTypeCell,
    meta: {
      styles: {
        minWidth: '250px',
        maxWidth: '250px',
        width: '250px',
      },
    },
  }),
  columnHelper.accessor('secondsSpent', {
    header: 'Time',
    cell: (props) => {
      const timeSpent = props.getValue();
      if (!timeSpent) return '-';

      return formatDuration(timeSpent);
    },
    meta: {
      styles: {
        minWidth: '100px',
        maxWidth: '100px',
        width: '100px',
        textAlign: 'right',
      },
    },
  }),
  columnHelper.display({
    id: 'testingFocus',
    header: 'Testing Focus',
    enableSorting: false,
    cell: QuestionSkillsReviewCell,
    meta: {
      styles: {
        minWidth: '450px',
        maxWidth: '600px',
      },
    },
  }),
];

export default function StepQuestionsResultsTable({
  step,
  answer,
  stepReview,
  didStartTest,
  testDimensions,
}: {
  step: AssessmentTestStep;
  answer: AssessmentTestStepAnswer | undefined;
  stepReview: AssessmentTestResult['scenarioEvaluations'][number] | null;
  didStartTest: boolean;
  testDimensions?: Array<{ id: string; name: string; label: string }>;
}) {
  const tableData: TestQuestionRow[] = React.useMemo(
    () =>
      parseScenarioQuestionRowData({
        step,
        answer,
        stepReview,
        didStartTest,
        testDimensions: testDimensions ?? [],
      }),
    [step, answer, stepReview, didStartTest, testDimensions]
  );

  const [isScenarioModalOpen, setIsScenarioModalOpen] = React.useState(false);
  const [selectedQuestion, setSelectedQuestion] =
    React.useState<TestQuestionRow | null>(null);

  return (
    <>
      <TableWrapper>
        {tableData.length > 0 ? (
          <Table
            initialPinning={{
              left: ['questionNumber', 'status'],
            }}
            data={tableData}
            columns={createColumns()}
            getRowId={(row) => row.id}
            getRowProps={(row) => ({
              onClick: () => setSelectedQuestion(row.original),
              style: { cursor: 'pointer' },
            })}
            className="step-questions-table"
            footer={
              <CollapsibleSectionFooter>
                <CollapsibleSectionLabel>
                  {tableData.length}{' '}
                  {tableData.length === 1 ? 'Question' : 'Questions'}
                </CollapsibleSectionLabel>
                <CollapsibleSectionFooterButton
                  label="See Questions"
                  onClick={() => {
                    setIsScenarioModalOpen(true);
                  }}
                  faIcon="fa-regular fa-up-right-from-square"
                />
              </CollapsibleSectionFooter>
            }
          />
        ) : (
          <div style={{ margin: '16px' }}>
            <div style={{ marginTop: 0, paddingBottom: 0 }}>
              No questions available for this step
            </div>
          </div>
        )}
      </TableWrapper>
      <ScenarioStepModal
        isOpen={isScenarioModalOpen}
        onClose={() => setIsScenarioModalOpen(false)}
        step={step}
        answer={answer}
      />
      <QuestionAnalysisModal
        isOpen={!!selectedQuestion}
        onClose={() => setSelectedQuestion(null)}
        question={selectedQuestion}
        step={step}
        stepReview={stepReview}
      />
    </>
  );
}

function parseScenarioQuestionRowData({
  step,
  answer,
  stepReview,
  didStartTest,
  testDimensions,
}: {
  step: AssessmentTestStep;
  answer: AssessmentTestStepAnswer | undefined;
  stepReview: AssessmentTestResult['scenarioEvaluations'][number] | null;
  didStartTest: boolean;
  testDimensions: Array<{ id: string; name: string; label: string }>;
}): Array<TestQuestionRow> {
  return step.sections.map((section, index) => {
    const sectionAnswer = answer?.sections.find(
      (answerSection) => answerSection.id === section.id
    );

    const secondsSpent = sectionAnswer?.timeIntervals
      ? computeTimeFromIntervals(sectionAnswer.timeIntervals)
      : 0;

    return {
      id: section.id,
      stepId: step.id,
      didStartTest,
      questionNumber: index + 1,
      secondsSpent,
      type: section.type,
      answer: sectionAnswer ?? null,
      section,
      stepReview: stepReview ?? null,
      stepObjectives: step.objectives ?? [],
      dimensions: testDimensions,
    };
  });
}
